/**
 * --- LARKI 3D VIEWER ---
 *
 * DOCS:
 * PointerraJWT: https://www.npmjs.com/package/@pointerra/pointerra-jwt?activeTab=readme
 * Pointerra API: https://static.pointerra.io/viewer/api_docs/api_reference/index.html
 */

import React, { useCallback, useEffect, useState } from 'react';
/* @ts-expect-error: untyped library */
import pointerraJwt from '@pointerra/pointerra-jwt';
import '@pointerra/pointerra-bootstrap';
import { Route, Router, Switch } from 'react-router-dom';
import axios, { AxiosError } from 'axios';
import { Provider, useDispatch, useSelector } from 'react-redux';
import { StepType, TourProvider, useTour } from '@reactour/tour';

import { getUser } from '../utilities/storage';
import Page from '../components/page/Page';
import text from '../text';
import { useQuery } from '../hooks/useQuery';
import { client as apiClient } from '../utilities/api';
import useUrlParam from '../mp/hooks/useUrlParam';
import routerHistory from '../routes/history';
import routes from '../routes/constants';
import { GET_STARTED_TOURS } from '../pages/dashboard/components/onboarding/tour';
import {
  alertError,
  getUserProfile,
  hideModal,
  showModal,
} from '../redux/actions';
import { INITIATE_TOUR_MODAL } from '../components/modal/InitiateTourModal';
import { amplitudeTrack, store } from '../utilities';
import TourContent from '../pages/dashboard/components/onboarding/tour/TourContent';
import Img from '../components/img/Img';
import NavBar from '../mp/components/NavBar';
import { useRedirect } from '../hooks/useRedirect';
import { isDemoJob, jobHasPointCloud } from '../utilities/job';
import { captureError } from '../utilities/error';
import VersioningAlert from '../components/alert/versioning';
import Alerts from '../components/alert';
import useUser from '../mp/hooks/useUser';
import { useGetUserProjectsQuery } from '../redux/rtk';
import Loading from '../components/loading/Loading';
import ShareLinkNav from '../pages/dashboard/components/navigation/navbar/ShareLinkNav';
import { AMPLITUDE_EVENTS } from '../constants/analytics';
import OpenBookmarkPage from '../pages/openBookmark';
import useLoadAmplitude from '../hooks/useLoadAmplitude';
import { START_TRIAL_MODAL } from '../components/modal/StartTrialModal/StartTrialModal';
import ModalManager from '../components/modal/ModalManager';
import Onboarding from '../pages/dashboard/components/onboarding';
import PrivateRoute from '../mp/components/PrivateRoute';
import { useMount } from 'react-use';
import useOnLoginEmailBanner from '../hooks/useOnLoginEmailBanner';

const POINTERRA_VIEWER_ID = 'cloud-viewer';
const POINT_CLOUD_ID_KEY = 'pcid';
const GOOGLE_3D_TILES_CONFIG = {
  id: 'wJmhkhTcaZrCpvacmZbFZS',
  name: 'Google 3D Tiles',
};

/* global pointerra */
declare global {
  const pointerra: any;
}

class ErrorWrapper<T extends Error> extends Error {
  originalError: T;
  constructor(originalError: T) {
    super(originalError.message);
    this.originalError = originalError;
  }
}

class JwtError extends ErrorWrapper<Error> { }
class UnauthorizedError extends ErrorWrapper<AxiosError> { }
class NotFoundError extends ErrorWrapper<AxiosError> { }
class NoPointCloudsError extends Error {
  constructor() {
    super('Project has no associated point clouds');
  }
}

const Viewer2 = () => {
  const { profile, canUserStartTrial, isTrialExpired } = useUser();
  useLoadAmplitude(profile?.id.toString());
  useOnLoginEmailBanner();

  const projectId = useUrlParam('projectId', { int: true });
  const queryParams = useQuery();
  const redirect = useRedirect();
  const [pointerraApi, setPointerraApi] = useState<any>(null);
  const [sceneDefaults, setSceneDefaults] = useState<any>(null);
  const [isPointCloudLoaded, setIsPointCloudLoaded] = useState(false);
  const [error, setError] = useState<
    JwtError | UnauthorizedError | NotFoundError | NoPointCloudsError | null
  >(null);
  const hasOnboarded = !!profile?.full_name && !!profile?.profession;

  // const { is3dViewerEnabled, isSuccess } = useUser();
  // const [userToken, setUserToken] = useState<string|null>(null);

  const tourSelected = useSelector(
    (state: any) => state.tourReducer.tourSelected
  );
  const { steps, setSteps, setCurrentStep } = useTour();
  const dispatch = useDispatch();

  // useEffect(() => {
  //   setUserToken(getUser()?.token ?? null);
  // }, []);

  const expiryCallback = useCallback(
    async (
      loginFunc: (username: string, password: string) => Promise<void>
    ) => {
      try {
        const userToken = getUser()?.token;
        if (!userToken)
          throw new Error('Attempted `loadClient` before `userToken` was set.');
        await loginFunc('', userToken);
      } catch (err) {
        if (err instanceof Error) setError(new JwtError(err));
        else throw err;
      }
    },
    []
  );

  const getProjectDataset = useCallback(
    async (
      config: { headers: { Authorization: string } },
      pointerraId: string,
      filter = '',
      url = null
    ) => {
      const { data } = await axios.get(
        url ??
        `${process.env.ENGINE_POINTERRA_API_URL}pointclouds/${pointerraId}/project/datasets${filter}`,
        config
      );
      return data;
    },
    []
  );

  const enableGoogle3dTilesDataset = useCallback(
    async (
      config: { headers: { Authorization: string } },
      pointerraId: string
    ) => {
      const { total: filteredByIdCount } = await getProjectDataset(
        config,
        pointerraId,
        `?id=${GOOGLE_3D_TILES_CONFIG.id}`
      );
      if (filteredByIdCount === 0) {
        console.log('--- Enabling Google 3D Tiles Dataset ---');
        const datasets = [];
        let nextPageUrl = null;
        let totalCount = 0;
        // defaulted to 10 per page
        // Loop through the end of the existing dataset pages and append to `datasets`
        do {
          const {
            total,
            data: existingDatasets,
            next_page_url,
          } = await getProjectDataset(config, pointerraId, '', nextPageUrl);
          totalCount = total;
          nextPageUrl = next_page_url;
          datasets.push(...existingDatasets);
        } while (nextPageUrl);
        await axios.patch(
          `${process.env.ENGINE_POINTERRA_API_URL}pointclouds/${pointerraId}/project`,
          {
            datasets: [
              ...datasets,
              {
                order: totalCount + 1,
                properties: '',
                dataset: {
                  name: GOOGLE_3D_TILES_CONFIG.name,
                  dataset_id: GOOGLE_3D_TILES_CONFIG.id,
                  status_reason: '',
                  hidden: false,
                  public: true,
                },
              },
            ],
          },
          config
        );
      }
    },
    [getProjectDataset]
  );

  useEffect(() => {
    // prevent loading the viewer when Onboarding modal is opened
    // data entry at Onboarding is laggy when viewer is loading
    if (!hasOnboarded) return;

    const { LARKI_API_URL } = process.env;
    if (!LARKI_API_URL) throw new Error('LARKI_API_URL not set');
    pointerraJwt.configure({
      endpoint: LARKI_API_URL,
      disableLoginDialog: true,
      storage: localStorage,
      expiryCallback,
    });

    Promise.all([
      pointerraJwt.headers(),
      pointerra.loadClient({
        // https://static.pointerra.io/viewer/api_docs/api_reference/interfaces/iclientoptions.html
        targetElement: `#${POINTERRA_VIEWER_ID}`,
        jwt: pointerraJwt,
        plugins: [
          pointerra.Plugins.LeafletMap(
            'https://tile.googleapis.com/v1/3dtiles/root.json?key='
          ),
          pointerra.Plugins.Datasets(),
          pointerra.Plugins.PanoViewer(),
          pointerra.Plugins.PhotosViewer(),
          pointerra.Plugins.SphericalImages(),
        ],
        viewer: {
          disallowedTools: ['VolumeMeasure', 'PlaneMeasure'],
          showScansPanel: false,
          enableTools: true,
          showFullscreenButton: false,
          showOptionsButton: false,
        },
        internal: {
          enableBookmarkLink: true,
        },
      }),
      new Promise((resolve, reject) => {
        // we can get the pcid from query params to make this more efficient
        const pcid = queryParams.get(POINT_CLOUD_ID_KEY);
        if (pcid) return resolve(pcid);
        else
          return apiClient
            .getProjectById(projectId, {
              includeJobs: true,
              includePointCloud: true,
            })
            .then(({ data }) => {
              const isDemo = data.jobs.length > 0 && isDemoJob(data.jobs[0]);
              if (isDemo) {
                console.info(
                  '--- Viewed demo project in 3D viewer and save to Amplitude ---'
                );
                if (profile?.id) {
                  amplitudeTrack(
                    AMPLITUDE_EVENTS.VIEWED_DEMO_PROJECT_IN_3D_VIEWER.EVENT,
                    {},
                    { user_id: Number(profile.id) } // explicitly set user_id since new Viewer component profileReducer is not set
                  );
                }
              }

              const pcid = data.point_clouds?.[0]?.pointerra_id;
              if (!pcid) return reject(new NoPointCloudsError());
              queryParams.set(POINT_CLOUD_ID_KEY, pcid);

              if (isTrialExpired) {
                redirect.push(routes.user.membershipManagement, {
                  preserveQueryParams: true,
                  queryParams: {
                    redirectTo: `${routes.view3DExternal.project(projectId)}`,
                    label: text('navigateToRoute', {
                      navigation: 'Continue',
                      routeName: text('viewer3D'),
                    }),
                  },
                });
              } else if (canUserStartTrial && hasOnboarded && !isDemo) {
                // If user can start trial and already onboarded and not demo, show trial modal
                // otherwise, show onboarding modal and start trial will be handled in onboarding modal
                dispatch(
                  showModal(START_TRIAL_MODAL, {
                    onHide: () => {
                      redirect.push(routes.dashboard);
                      dispatch(hideModal(START_TRIAL_MODAL));
                    },
                    onStartTrial: () => {
                      dispatch(hideModal(START_TRIAL_MODAL));
                    },
                  })
                );
              }

              return resolve(pcid);
            })
            .catch((err) => {
              if (err instanceof AxiosError) {
                const status = err.response?.status;
                if (status === 401 || status === 403) {
                  return reject(new UnauthorizedError(err));
                } else if (status === 404) {
                  return reject(new NotFoundError(err));
                }
              }
              return reject(err);
            });
      }),
    ])
      .then(
        async ([headers, api, pcid]: [
          { Authorization: string },
          any,
          string,
        ]) => {
          if (!api) throw new Error('Failed to load Pointerra client');
          setPointerraApi(api);

          enableGoogle3dTilesDataset({ headers }, pcid).catch((err) => {
            throw err;
          });

          api.pointcloud.load(pcid);

          api.pointcloud.events.loaded.addEventListener(() => {
            setIsPointCloudLoaded(true);
          });

          api.bookmarks.setBookmarkLinkCallback((id: string) => {
            const viewerPage = routes.view3DExternal.project(projectId);
            return `${viewerPage}?bookmark=${id}`;
          });

          const bookmark = queryParams.get('bookmark');
          if (bookmark) {
            api.bookmarks.events.loaded.addEventListener(() => {
              api.bookmarks.open(bookmark);
            });
          }

          const { data: defaults } = await axios.get(
            `${process.env.ENGINE_POINTERRA_API_URL}pointclouds/${pcid}/scene_defaults`,
            {
              headers,
            }
          );
          setSceneDefaults(defaults);
        }
      )
      .catch((err) => {
        if (
          err instanceof UnauthorizedError ||
          err instanceof NotFoundError ||
          err instanceof NoPointCloudsError
        )
          setError(err);
        throw err;
      });
  }, [
    canUserStartTrial,
    dispatch,
    enableGoogle3dTilesDataset,
    expiryCallback,
    isTrialExpired,
    profile?.id,
    projectId,
    queryParams,
    redirect,
    hasOnboarded,
  ]);

  useEffect(() => {
    if (!pointerraApi) return;
    if (isPointCloudLoaded && sceneDefaults) {
      console.log('--- Setting scene properties ---');
      if (sceneDefaults.scene_properties) {
        pointerraApi.scene.setProperties(sceneDefaults.scene_properties);
      }
    }
  }, [isPointCloudLoaded, pointerraApi, sceneDefaults]);

  useEffect(() => {
    if (isPointCloudLoaded) {
      if (tourSelected) {
        setSteps?.(GET_STARTED_TOURS[tourSelected] as StepType[]);
        setCurrentStep(0);
      }

      if (steps.length) {
        dispatch(showModal(INITIATE_TOUR_MODAL));
      }
    }
  }, [
    dispatch,
    isPointCloudLoaded,
    setCurrentStep,
    setSteps,
    steps.length,
    tourSelected,
  ]);

  useEffect(() => {
    if (error) {
      if (error instanceof UnauthorizedError) {
        console.error('Unauthorized access to this viewer.');
        dispatch(alertError('Unauthorized access to this viewer.'));
        redirect.push(routes.fileManager.viewProject(projectId));
        return;
      }
      captureError(error);
      dispatch(
        alertError('Error loading viewer. Developers have been notified.')
      );
    }
  }, [error, dispatch, redirect, projectId]);

  // useEffect(() => {
  //   if (isSuccess && !is3dViewerEnabled) {
  //     console.error('Unauthorized access to this viewer. 3d viewer not enabled for this user.');
  //     redirect.push(routes.fileManager.viewProject(projectId));
  //     dispatch(alertError('Unauthorized access to this viewer.'));
  //   }
  // }, [dispatch, is3dViewerEnabled, isSuccess, projectId, redirect]);

  // legacy user profile fetch since Onboardings isUserLogged is dependent on it
  useMount(() => {
    dispatch(getUserProfile());
  });

  return (
    <>
      <NavBar.Provider>
        <NavBar.Brand />
        <NavBar.ProjectSelect
          projectId={projectId}
          onJobChange={(next) => {
            redirect.push(routes.view3DExternal.project(next.project.id));
            window.location.reload();
          }}
          filterPredicate={(project) => jobHasPointCloud(project)}
        />
        {/* @ts-expect-error: untyped component */}
        <ShareLinkNav show />
        <NavBar.ViewModes projectId={projectId} />
        <NavBar.UserNav />
      </NavBar.Provider>
      {/* @ts-expect-error: untyped component */}
      <Page
        className='PointerraViewer'
        title={text('viewer3D')}
        showTitlebar={false}
      >
        <div
          className='viewer-bg'
          data-spinner={isPointCloudLoaded ? 'false' : 'true'}
        >
          <div
            id={POINTERRA_VIEWER_ID}
            className='Viewer-pointerra'
            tabIndex={0}
          />
          <div className='pointerra-logo'>
            <Img img='pointerra-logo' />
          </div>
        </div>
        <Onboarding
          submitButtonText={`Continue to ${text('trial3DViewerBenefit')}`}
        />
      </Page>
    </>
  );
};

const Viewer2Redirect = () => {
  const dispatch = useDispatch();
  const redirect = useRedirect();
  const { isAdmin, isSuccess } = useUser();
  const {
    isLoading,
    status,
    data: latestProject,
  } = useGetUserProjectsQuery(
    // includeDemo: true so when user with no completed project/order but elligible to accesss 3D viewer is redirected to Demo
    { isAdmin, includeDemo: true },
    {
      // only fetch projects once user query has completed, since `isAdmin` is a dependency
      skip: !isSuccess,
      // select the latest project
      selectFromResult: (res) => {
        const obj = res.data?.projects;
        if (!obj) return { ...res, data: null };
        const arr = Object.values(obj)
          .filter((project) => jobHasPointCloud(project))
          .sort((a, b) => {
            return (
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
            );
          });
        return { ...res, data: arr[0]?.project ?? null };
      },
    }
  );
  useEffect(() => {
    if (status === 'fulfilled') {
      if (latestProject) {
        redirect.push(routes.view3DExternal.project(latestProject.id));
      } else {
        dispatch(alertError('No projects with 3D enabled'));
        redirect.push(routes.dashboard);
      }
    }
  }, [status, latestProject, redirect, dispatch]);
  if (isLoading) return <Loading />;
  return null;
};

const Viewer2App = () => {
  return (
    <Provider store={store}>
      <TourProvider
        steps={[]}
        // @ts-expect-error: untyped component
        ContentComponent={TourContent}
        onClickMask={() => { }}
      >
        <Router history={routerHistory}>
          <div className='alert-container text-center w-100'>
            <VersioningAlert />
            <Alerts />
          </div>
          <Switch>
            <Route exact path='/viewer' component={Viewer2Redirect} />
            <Route path='/viewer/:projectId' component={Viewer2} />
            <PrivateRoute
              path={routes.openBookmark.path}
              component={OpenBookmarkPage}
            />
          </Switch>
          <ModalManager />
        </Router>
      </TourProvider>
    </Provider>
  );
};

export default Viewer2App;
